import { useState, useRef, useEffect, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { Loader2, AlertCircle, Sun, Moon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { organizationsApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme } from '@/lib/theme-provider'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'

const MAX_ORG_NAME_LENGTH = 60

type ThemeOption = 'light' | 'dark'

interface ThemeCardProps {
  value: ThemeOption
  icon: React.ElementType
  label: string
  selected: boolean
  onSelect: () => void
}

function ThemeCard({ value, icon: Icon, label, selected, onSelect }: ThemeCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={`account-setup-theme-${value}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(
        'flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-5 transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:border-muted-foreground/30 hover:bg-muted/50',
      )}
    >
      <Icon className={cn('h-6 w-6', selected ? 'text-primary' : 'text-muted-foreground')} />
      <Text size="sm" weight="medium">
        {label}
      </Text>
    </button>
  )
}

export const AccountSetupStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function AccountSetupStep({ wizardState, onUpdateState, onNext, className }, ref) {
    const { t } = useTranslation(['onboarding', 'common'])
    const [orgName, setOrgName] = useState(wizardState.orgName || '')
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const shouldReduceMotion = useReducedMotion()

    const { user } = useAuth()
    const { theme, setTheme } = useTheme()
    const firstName = user?.name?.split(' ')[0] ?? ''
    const isInvited = wizardState.isInvitedUser

    // Auto-focus org name input after entrance animation
    useEffect(() => {
      if (isInvited) return
      const timer = setTimeout(() => inputRef.current?.focus(), 600)
      return () => clearTimeout(timer)
    }, [isInvited])

    function handleOrgNameChange(value: string) {
      if (value.length <= MAX_ORG_NAME_LENGTH) {
        setOrgName(value)
        setError(null)
      }
    }

    async function handleSubmit() {
      // The picker preselects the system theme; continuing confirms it as the user's choice.
      if (isInvited) {
        setTheme(theme)
        onNext()
        return
      }

      const trimmed = orgName.trim()
      if (!trimmed) {
        setError('Organization name is required')
        return
      }

      if (!wizardState.orgId) {
        setError('No organization found. Please refresh and try again.')
        return
      }

      setIsSaving(true)
      setError(null)

      try {
        await organizationsApi.update(wizardState.orgId, { name: trimmed })
        onUpdateState({ orgName: trimmed })
        setTheme(theme)
        onNext()
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t('account.saveFailed')
        setError(message)
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-8 outline-none', className)}>
        {/* Personalized greeting — delay 0 */}
        <FadeUp delay={0} className="text-center">
          <Heading level={1} size="4xl">
            {firstName ? t('account.welcomeNamed', { name: firstName }) : t('account.welcome')}
          </Heading>
        </FadeUp>

        {/* Org section — delay 120ms */}
        {isInvited ? (
          <FadeUp delay={120} className="text-center">
            <Text size="base" color="muted">
              {t('account.joining')}{' '}
              <span className="font-semibold text-foreground">{wizardState.orgName}</span>
            </Text>
          </FadeUp>
        ) : (
          <FadeUp delay={120} className="flex flex-col gap-1">
            <Text size="sm" color="muted" className="mb-1">
              {t('account.nameWorkspace')}
            </Text>
            <Input
              ref={inputRef}
              id="org-name-input"
              data-testid="account-setup-org-name-input"
              variant="featured"
              value={orgName}
              onChange={(e) => handleOrgNameChange(e.target.value)}
              placeholder={t('account.orgPlaceholder')}
              maxLength={MAX_ORG_NAME_LENGTH}
              disabled={isSaving}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
            />
            <Text size="xs" color="muted" className="mt-1 text-right tabular-nums">
              {orgName.length}/{MAX_ORG_NAME_LENGTH}
            </Text>
          </FadeUp>
        )}

        {/* Theme picker — delay 240ms */}
        <FadeUp delay={240} className="flex flex-col gap-2">
          <Text size="sm" color="muted">
            {t('account.chooseTheme')}
          </Text>
          <div
            role="radiogroup"
            aria-label={t('account.themeAria')}
            className="flex gap-3"
            data-testid="account-setup-theme-picker"
          >
            <ThemeCard
              value="light"
              icon={Sun}
              label={t('account.light')}
              selected={theme === 'light'}
              onSelect={() => setTheme('light')}
            />
            <ThemeCard
              value="dark"
              icon={Moon}
              label={t('account.dark')}
              selected={theme === 'dark'}
              onSelect={() => setTheme('dark')}
            />
          </div>
        </FadeUp>

        {/* Inline error */}
        {error && (
          <motion.div
            role="alert"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
            className="flex items-center gap-2 text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <Text size="sm" color="destructive">{error}</Text>
          </motion.div>
        )}

        {/* CTA button — delay 360ms */}
        <FadeUp delay={360}>
          <Button
            data-testid="account-setup-continue-button"
            variant="cta"
            onClick={handleSubmit}
            disabled={isSaving || (!isInvited && !orgName.trim())}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('saving', { ns: 'common' })}
              </>
            ) : (
              t('continue', { ns: 'common' })
            )}
          </Button>
        </FadeUp>
      </div>
    )
  },
)
